import React from 'react';
import { SectionWrapper } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { FormatAsCurrencyForLocale } from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { SLICES } from '../../../constants/slices';
import { PER_FREQUENCY_LABELS, FREQUENCY_CODE_ANNUALLY } from '../../../constants/constants';
import Loading from '../../shared/Loading/Loading';
import { RENEWAL_HUB_CONTENT, RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP } from '../../../constants/cmsCodeName';
import { getClaimingExample } from './summary';
import './adjustedRenewalSummary.scss';
import { RenewalDataPropType } from '../../../propTypes/renewalData';
import { ERRORS } from '../consts';
import { transformPanelSummaryContent } from '../../../utils/transformContent';

export const AdjustedRenewalSummary = ({ renewalData, isLoading, cmsData }) => {
  const { adjustedSummary } = useSelector((state) => state?.[SLICES.RENEWAL_HUB]);
  const { [RENEWAL_HUB_CONTENT.PANEL_SUMMARY]: panelSummary } = cmsData || {};

  if (isLoading || !renewalData)
    return (
      <SectionWrapper
        heading="Adjusted renewal summary"
        customCssClass="adjustedRenewalSummary"
        isFixed
      >
        {!renewalData && <p>{ERRORS.loadingError}</p>}
        {isLoading && <Loading />}
      </SectionWrapper>
    );
  const { PetName, PaymentFrequency, ExcessType } = renewalData;
  const excessTypeLabel = (ExcessType || 'excess').toLowerCase();

  const newPremium = adjustedSummary?.newPremium ?? 0;
  const freeMonthDiscount = adjustedSummary?.freeMonthDiscount ?? 0;
  const newPremiumAnnual = adjustedSummary?.newPremiumAnnual ?? 0;
  const premiumDifference = newPremiumAnnual - (renewalData?.PremiumAnnual ?? 0);
  const premiumDifferenceLabel = premiumDifference >= 0 ? 'increase' : 'decrease';
  const premiumDifferenceAmount = Math.abs(premiumDifference);

  const { youPay: currentYouPay } = getClaimingExample(renewalData);
  const {
    billAmount,
    afterBenefitPercentage,
    afterExess,
    wePay,
    youPay: adjustedYouPay
  } = getClaimingExample(adjustedSummary);

  const excessDifference = adjustedYouPay - currentYouPay;
  let excessDifferenceLabel = 'will be';

  if (excessDifference > 0) {
    excessDifferenceLabel = `will increase from ${FormatAsCurrencyForLocale(
      currentYouPay
    )} to ${FormatAsCurrencyForLocale(adjustedYouPay, { trimZeroDecimals: true })}`;
  } else if (excessDifference < 0) {
    excessDifferenceLabel = `will decrease from ${FormatAsCurrencyForLocale(currentYouPay, {
      trimZeroDecimals: true
    })} to ${FormatAsCurrencyForLocale(adjustedYouPay, { trimZeroDecimals: true })}`;
  }

  const panelSummaryValues = {
    csp_premiumDiff: premiumDifferenceAmount,
    premiumAnnualNewValue: newPremiumAnnual,
    premiumAnnualOldValue: renewalData?.PremiumAnnual ?? 0,
    premium: currentYouPay,
    csp_newPremium: adjustedYouPay
  };
  return (
    <SectionWrapper
      heading="Adjusted renewal summary"
      customCssClass="adjustedRenewalSummary"
      isFixed
    >
      <div>
        <div className="collectionAmount">
          {PaymentFrequency?.toLowerCase() === FREQUENCY_CODE_ANNUALLY.toLowerCase() ? (
            <>
              <span>New collection amount (annual)</span>
              <strong>{FormatAsCurrencyForLocale(newPremium - freeMonthDiscount, { trimZeroDecimals: true })}</strong>
            </>
          ) : (
            <>
              <span>New collection amount (per {PER_FREQUENCY_LABELS[PaymentFrequency]?.toLowerCase()})</span>
              <strong>{FormatAsCurrencyForLocale(newPremium, { trimZeroDecimals: true })}</strong>
            </>
          )}
        </div>
        <div className="summaryContent">
          {transformPanelSummaryContent(
            `By adjusting your cover, your annual premium will ${premiumDifferenceLabel} by ${FormatAsCurrencyForLocale(
              premiumDifferenceAmount
            )}, and your out-of-pocket claim costs (for non-Routine Care claims) ${excessDifferenceLabel} as shown in the claim example above and below.`,
            panelSummaryValues,
            RENEWAL_HUB_CONTENT_PLACEHOLDER_KEY_MAP,
            panelSummary
          )}
        </div>
        <SectionWrapper
          heading="Claiming example"
          customCssClass="claimingExample"
        >
          <ul>
            <li>
              <span>
                <strong>{`${PetName}’s vet bill is ${FormatAsCurrencyForLocale(billAmount, {
                  trimZeroDecimals: true
                })}`}</strong>
              </span>
              <span />
            </li>
            <li>
              <span>Apply {`${adjustedSummary?.BenefitPercentage || 0}%`} benefit percentage:</span>
              <span>{FormatAsCurrencyForLocale(afterBenefitPercentage, { trimZeroDecimals: true })}</span>
            </li>
            <li>
              <span>{`Subtract ${FormatAsCurrencyForLocale(adjustedSummary?.Excess || 0, {
                trimZeroDecimals: true
              })} ${excessTypeLabel}:`}</span>
              <span>{FormatAsCurrencyForLocale(afterExess, { trimZeroDecimals: true })}</span>
            </li>
            <li>
              <strong>We pay:</strong>
              <strong>{FormatAsCurrencyForLocale(wePay, { trimZeroDecimals: true })}</strong>
            </li>
            <li>
              <strong>You pay:</strong>
              <strong>{FormatAsCurrencyForLocale(adjustedYouPay, { trimZeroDecimals: true })}</strong>
            </li>
          </ul>
        </SectionWrapper>
      </div>
    </SectionWrapper>
  );
};

AdjustedRenewalSummary.propTypes = {
  renewalData: RenewalDataPropType,
  isLoading: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  cmsData: PropTypes.object
};
